import { z } from "zod";

export const UserRoleSchema = z.enum(["ADMIN", "COORDINATOR", "SUB_TEAM_LEADER", "REVIEWER", "OBSERVER"]);
export type UserRole = z.infer<typeof UserRoleSchema>;

// Roles a COORDINATOR may create or modify. COORDINATOR/ADMIN accounts and role
// changes stay ADMIN-only, alongside every delete action. Shared by backend
// role-management routes (users.ts, auth.ts) and the frontend Admin UI so the
// rule lives in exactly one place.
export const COORDINATOR_MANAGEABLE_ROLES = ["OBSERVER", "REVIEWER", "SUB_TEAM_LEADER"] as const;

// Shared optional researcher profile fields
const ProfileFields = {
  orcidId:     z.string().nullable().optional(),
  affiliation: z.string().nullable().optional(),
  department:  z.string().nullable().optional(),
  country:     z.string().nullable().optional(),
  phone:       z.string().nullable().optional(),
  website:     z.string().url().nullable().optional(),
  bio:         z.string().nullable().optional(),
  expertiseAreas: z.array(z.string()).optional(),
};

export const UserSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  name: z.string().min(1),
  role: UserRoleSchema,
  mustChangePassword: z.boolean().default(false),
  ...ProfileFields,
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type User = z.infer<typeof UserSchema>;

export const CreateUserSchema = z.object({
  email: z.string().email(),
  name: z.string().min(1),
  password: z.string().min(8),
  role: UserRoleSchema.default("REVIEWER"),
  ...ProfileFields,
});
export type CreateUser = z.infer<typeof CreateUserSchema>;

export const UpdateUserSchema = z.object({
  name: z.string().min(1).optional(),
  role: UserRoleSchema.optional(),
  ...ProfileFields,
});
export type UpdateUser = z.infer<typeof UpdateUserSchema>;

export const LoginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
});
export type Login = z.infer<typeof LoginSchema>;

export const AuthTokensSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
  user: UserSchema,
});
export type AuthTokens = z.infer<typeof AuthTokensSchema>;
